import { motion } from "framer-motion";
import { useState } from "react";

export default function NewsletterCTA() {
  const [inputValue, setInputValue] = useState("");
  const [inputType, setInputType] = useState("text");
  const [isValid, setIsValid] = useState(true);
  const [validationMessage, setValidationMessage] = useState("");

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    // Remove all non-digit characters for validation
    const cleanPhone = phone.replace(/\D/g, "");
    // Accept various phone number formats (7-15 digits)
    return cleanPhone.length >= 7 && cleanPhone.length <= 15;
  };

  const detectInputType = (value) => {
    // If it contains @, treat as email
    if (value.includes("@")) {
      return "email";
    }
    // If it contains only digits, spaces, dashes, parentheses, or +, treat as phone
    if (
      /^[\d\s\-\(\)\+]+$/.test(value) &&
      value.replace(/\D/g, "").length >= 3
    ) {
      return "tel";
    }
    // Default to text for mixed content
    return "text";
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);

    if (value === "") {
      setIsValid(true);
      setValidationMessage("");
      setInputType("text");
      return;
    }

    const detectedType = detectInputType(value);
    setInputType(detectedType);

    let valid = false;
    let message = "";

    if (detectedType === "email") {
      valid = validateEmail(value);
      message = valid ? "" : "Please enter a valid email address";
    } else if (detectedType === "tel") {
      valid = validatePhone(value);
      message = valid ? "" : "Please enter a valid phone number";
    } else {
      // For mixed or unclear input, be more lenient
      valid = value.length >= 3;
      message = valid ? "" : "Please enter at least 3 characters";
    }

    setIsValid(valid);
    setValidationMessage(message);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!inputValue.trim()) {
      setIsValid(false);
      setValidationMessage("Please enter your email or phone number");
      return;
    }

    if (!isValid) {
      return;
    }

    // Here you would typically send the data to your backend
    console.log(`Submitting ${inputType}:`, inputValue);

    // Reset form after successful submission
    setInputValue("");
    setIsValid(true);
    setValidationMessage("");
    setInputType("text");

    // You could add a success message here
    alert(`Thank you! We'll contact you at ${inputValue}`);
  };
  return (
    <section className="relative py-16 bg-black sm:py-20">
      {/* Dotted background */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.12)_1px,transparent_1px)] [background-size:16px_16px]" />

      {/* Decorative arcs (subtle) */}
      <div className="absolute -top-20 -left-20 w-60 h-60 rounded-full border-8 border-emerald-600/30" />
      <div className="absolute -right-24 -bottom-24 w-64 h-64 rounded-full border-8 border-emerald-600/20" />

      <div className="relative px-6 mx-auto max-w-3xl text-center">
        <motion.h2
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-2xl font-extrabold leading-tight text-emerald-400 sm:text-3xl md:text-4xl"
        >
          Get travel safety tips, policy
          <br className="hidden sm:block" /> updates & offers
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1, duration: 0.5 }}
          className="mt-3 text-sm text-gray-300 md:text-base"
        >
          Elevate your travel experience with tips, news, and special offer
          delivered to you
        </motion.p>

        <motion.form
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2, duration: 0.5 }}
          onSubmit={handleSubmit}
          className="mx-auto mt-6 w-full max-w-2xl"
        >
          <div className="flex gap-2 items-center p-2 bg-white rounded-xl shadow-lg">
            <input
              type={inputType}
              value={inputValue}
              onChange={handleInputChange}
              placeholder="Enter your email address or phone number"
              className={`flex-1 px-4 w-full h-12 text-sm text-gray-900 rounded-lg placeholder:text-gray-400 focus:outline-none transition-colors ${
                !isValid && inputValue ? "border-2 border-red-500" : ""
              }`}
            />
            <button
              type="submit"
              className="px-6 h-12 font-semibold text-white bg-emerald-600 rounded-lg transition shrink-0 hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={!isValid || !inputValue.trim()}
            >
              Submit
            </button>
          </div>

          {/* Validation message */}
          {validationMessage && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-2 text-sm text-red-400 text-center"
            >
              {validationMessage}
            </motion.p>
          )}

          {/* Input type indicator */}
          {inputValue && isValid && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-1 text-xs text-gray-400 text-center"
            >
              {inputType === "email"
                ? "📧 Email detected"
                : inputType === "tel"
                ? "📱 Phone number detected"
                : "✉️ Enter email or phone number"}
            </motion.p>
          )}
        </motion.form>
      </div>
    </section>
  );
}
